import { CurrencyPipe, formatDate } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { EmailPayloadBase } from "@models/email-templates/email-payload-base";
import { EmailTemplateCodesToTypes } from "@models/email-templates/email-templates-helper";
import {
  AnnualStatementDebtReminderEmailPayload
} from "@models/email-templates/payloads/annual-statement-debt-reminder-email-payload";
import { AnnualStatementEmailPayload } from "@models/email-templates/payloads/annual-statement-email-payload";
import {
  AnnualStatementEsignEmailPayload
} from "@models/email-templates/payloads/annual-statement-esign-email-payload";
import { AsicDebtReminderEmailPayload } from "@models/email-templates/payloads/asic-debt-reminder-email-payload";
import { AsicDocumentsEmailPayload } from "@models/email-templates/payloads/asic-documents-email-payload";
import { AsicDocumentsEsignEmailPayload } from "@models/email-templates/payloads/asic-documents-esign-email-payload";
import { AsicInvoiceEmailPayload } from "@models/email-templates/payloads/asic-invoice-email-payload";
import { AnnualStatement } from "@models/annual-statements/annual-statement";
import { ChangeAuthorisation } from "@models/documents/change-authorisation";
import { Company } from "@models/companies/company";
import { FeeValueConfig } from "@models/configs/fee-value-config";
import { DocumentBase } from "@models/documents/document-base";
import { LodgementDeadline } from "@models/documents/lodgement-deadline";
import { UIProfile } from "@models/organisations/ui-profile";
import { CompanyForm362 } from "../../forms/models/company";
import { DateFormatPipe, DateFormatType } from "../pipes/dateFormatPipe";
import { DocumentTypePipe } from "../pipes/document-type.pipe";
import { ConfigSettingsService } from "./config-settings.service";
import { OrganisationApiService } from "./organisation-api.service";

interface IPrefillResult{
  payload: EmailPayloadBase;
  ignoredValueSubstitution: string[];
}

@Injectable({
    providedIn: 'root'
})
export class EmailTemaplatePrefillService{
  #documentTypePipe = inject(DocumentTypePipe);
  #organisationService = inject(OrganisationApiService);
  #dateFormatPipe = inject(DateFormatPipe);
  #currencyPipe = inject(CurrencyPipe);
  #configSettingsService = inject(ConfigSettingsService);

  profile: UIProfile | undefined;
  feeValueConfig!: FeeValueConfig;

  constructor(){
    this.profile = this.#organisationService.getCachedProfile();
    this.#configSettingsService.getFeeConfig().subscribe(config => {
      this.feeValueConfig = config;
    });
    if(!this.profile){
      this.#organisationService.getProfile().subscribe(profile => this.profile = profile);
    }
  }

  fillPayload(code: string, documentBase: DocumentBase, lodgement: LodgementDeadline | undefined): IPrefillResult{
    const type = EmailTemplateCodesToTypes[code];
    switch(type){
      case AsicDocumentsEsignEmailPayload.$type:
        return this.prefillAsicDocumentsEsignEmailPayload(documentBase, lodgement!);
      case AsicInvoiceEmailPayload.$type:
        return this.prefillAsicInvoiceEmailPayload(documentBase, lodgement!);
      case AsicDocumentsEmailPayload.$type:
        return this.prefillAsicDocumentsEmailPayload(documentBase, lodgement!);
      case AnnualStatementEmailPayload.$type:
        return this.prefillAnnualStatementEmailPayload(documentBase, lodgement!);
      case AnnualStatementEsignEmailPayload.$type:
        return this.prefillAnnualStatementEsignEmailPayload(documentBase, lodgement!);
      default:  throw new Error(`Email template type ${type} is not supported`);
    }
  }

  fillAdHocReminderPayload(code: string, company: Company, changeAuthorisation: ChangeAuthorisation): EmailPayloadBase{
    const type = EmailTemplateCodesToTypes[code];
    switch(type){
      case AnnualStatementDebtReminderEmailPayload.$type:
        return this.prefillAnnualStatementDebtReminderEmailPayload(company, changeAuthorisation);
      case AsicDebtReminderEmailPayload.$type:
        return this.prefillAsicDebtReminderEmailPayload(company, changeAuthorisation);
      default:  throw new Error(`Email template type ${type} is not supported`);
    }
  }

  prefillAsicDebtReminderEmailPayload(company: Company, changeAuthorisation: ChangeAuthorisation): EmailPayloadBase {
    const payload = new AsicDebtReminderEmailPayload();
    payload.companyACN = company.acn;
    payload.companyName = company.name;

    payload.organisationName = this.profile!.organisationName ?? 'N/A';
    payload.organisationEmail = this.profile!.organisationEmail ?? 'N/A';
    payload.organisationPhone = this.profile!.organisationPhone ?? 'N/A';

    payload.billingContactFirstName = company.billingContact?.firstName ?? 'N/A';
    payload.billingContactFullName = company.billingContact?.fullName ?? 'N/A';

    payload.partner = this.profile!.users?.find(user => user.id === company.partnerManagerId)?.fullName ?? 'N/A';
    payload.accountManager = this.profile!.users?.find(user => user.id === company.accountManagerId)?.fullName ?? 'N/A';
    payload.asicContact = '' //TODO get from settings, need to refactor asic contact as it is string

    payload.directorFirstName = changeAuthorisation?.authorisingParty?.individualDataOverride?.firstName ?? changeAuthorisation?.chairperson?.individualDataOverride?.firstName ?? 'N/A',
    payload.directorFullName = changeAuthorisation?.authorisingParty?.individualDataOverride?.fullNameLabel ?? changeAuthorisation?.chairperson?.individualDataOverride?.fullNameLabel ?? 'N/A',
    payload.username = this.#organisationService.getCurrentUser()?.fullName ?? 'N/A';

    payload.payUrl = '';

    return payload;
  }

  prefillAnnualStatementDebtReminderEmailPayload(company: Company, changeAuthorisation: ChangeAuthorisation): EmailPayloadBase {
    const payload = new AnnualStatementDebtReminderEmailPayload();
    payload.companyACN = company.acn;
    payload.companyName = company.name;

    payload.organisationName = this.profile!.organisationName ?? 'N/A';
    payload.organisationEmail = this.profile!.organisationEmail ?? 'N/A';
    payload.organisationPhone = this.profile!.organisationPhone ?? 'N/A';

    payload.billingContactFirstName = company.billingContact?.firstName ?? 'N/A';
    payload.billingContactFullName = company.billingContact?.fullName ?? 'N/A';

    payload.partner = this.profile!.users?.find(user => user.id === company.partnerManagerId)?.fullName ?? 'N/A';
    payload.accountManager = this.profile!.users?.find(user => user.id === company.accountManagerId)?.fullName ?? 'N/A';
    payload.asicContact = '' //TODO get from settings, need to refactor asic contact as it is string

    payload.directorFirstName = changeAuthorisation?.authorisingParty?.individualDataOverride?.firstName ?? changeAuthorisation?.chairperson?.individualDataOverride?.firstName ?? 'N/A',
    payload.directorFullName = changeAuthorisation?.authorisingParty?.individualDataOverride?.fullNameLabel ?? changeAuthorisation?.chairperson?.individualDataOverride?.fullNameLabel ?? 'N/A',
    payload.username = this.#organisationService.getCurrentUser()?.fullName ?? 'N/A';

    payload.payUrl = '';

    return payload;
  }

  prefillAsicDocumentsEsignEmailPayload(documentBase: DocumentBase, lodgement: LodgementDeadline): IPrefillResult {
    const payload = new AsicDocumentsEsignEmailPayload();

    const emailPayload = this.prefillAsicDocumentsEmailPayload(documentBase, lodgement);

    Object.assign(payload, this.omitProperty(emailPayload.payload, '$type'));

    payload.signUrl = '';

    return {payload, ignoredValueSubstitution: emailPayload.ignoredValueSubstitution};
  }

  prefillAsicInvoiceEmailPayload(documentBase: DocumentBase, lodgement: LodgementDeadline): IPrefillResult {
    const payload = new AsicInvoiceEmailPayload();

    payload.companyACN = documentBase.company.acn;
    payload.companyName = documentBase.company.name;

    payload.documentName = `ASIC ${this.#documentTypePipe.transform(documentBase.type)}`;
    payload.documentType = this.#documentTypePipe.transform(documentBase.type);

    const signatories = documentBase.documentSigning?.signatures[0]
    payload.directorFirstName = signatories?.signatory?.firstName ?? 'N/A';
    payload.directorFullName = signatories?.signatory?.fullNameLabel ?? 'N/A';

    payload.organisationName = this.profile!.organisationName ?? 'N/A';
    payload.organisationEmail = this.profile!.organisationEmail ?? 'N/A';
    payload.organisationPhone = this.profile!.organisationPhone ?? 'N/A';
    payload.username = this.#organisationService.getCurrentUser()?.fullName ?? 'N/A';

    payload.billingContactFirstName = documentBase.company.billingContact?.firstName ?? 'N/A';
    payload.billingContactFullName = documentBase.company.billingContact?.fullName ?? 'N/A';

    payload.partner = this.profile!.users?.find(user => user.id === documentBase.company.partnerManagerId)?.fullName ?? 'N/A';
    payload.accountManager = this.profile!.users?.find(user => user.id === documentBase.company.accountManagerId)?.fullName ?? 'N/A';
    payload.asicContact = '';

    const ignoredValueSubstitution: string[] = ["directorFirstName", "directorFullName"];
    return {payload, ignoredValueSubstitution};
  }

  prefillAsicDocumentsEmailPayload(documentBase: DocumentBase, lodgement: LodgementDeadline): IPrefillResult {
    const payload = new AsicDocumentsEmailPayload();

    if (documentBase.changes?.length > 0 && documentBase.changes[0].$type === 'CompanyForm362') {
      const form = documentBase.changes[0] as CompanyForm362;
      payload.companyACN = form.companyACN;
      payload.companyName = form.companyName;
    } else {
      payload.companyACN = documentBase.company.acn;
      payload.companyName = documentBase.company.name;
    }

    payload.documentName = `ASIC ${this.#documentTypePipe.transform(documentBase.type)}`;
    payload.documentType = this.#documentTypePipe.transform(documentBase.type);

    const signatories = documentBase.documentSigning?.signatures[0]
    payload.directorFirstName = signatories?.signatory?.firstName ?? 'N/A';
    payload.directorFullName = signatories?.signatory?.fullNameLabel ?? 'N/A';

    payload.organisationName = this.profile!.organisationName ?? 'N/A';
    payload.organisationEmail = this.profile!.organisationEmail ?? 'N/A';
    payload.organisationPhone = this.profile!.organisationPhone ?? 'N/A';
    payload.username = this.#organisationService.getCurrentUser()?.fullName ?? 'N/A';

    payload.lodgementDeadline = this.#dateFormatPipe.transform(lodgement.lodgementDeadline, DateFormatType.Long);
    payload.lodgementDeadlineShortFormat = this.#dateFormatPipe.transform(lodgement.lodgementDeadline, DateFormatType.Short);
    const tenDaysBeforeLodgement = new Date(lodgement.lodgementDeadline);
    tenDaysBeforeLodgement.setDate(tenDaysBeforeLodgement.getDate() - 10);
    payload.tenDaysBeforeLodgementDeadline = this.#dateFormatPipe.transform(tenDaysBeforeLodgement, DateFormatType.Long);
    payload.tenDaysBeforeLodgementDeadlineShortFormat = this.#dateFormatPipe.transform(tenDaysBeforeLodgement, DateFormatType.Short);

    payload.billingContactFirstName = documentBase.company.billingContact?.firstName ?? 'N/A';
    payload.billingContactFullName = documentBase.company.billingContact?.fullName ?? 'N/A';

    payload.partner = this.profile!.users?.find(user => user.id === documentBase.company.partnerManagerId)?.fullName ?? 'N/A';
    payload.accountManager = this.profile!.users?.find(user => user.id === documentBase.company.accountManagerId)?.fullName ?? 'N/A';
    payload.asicContact = '';

    const ignoredValueSubstitution: string[] = ["directorFirstName", "directorFullName"];
    return {payload, ignoredValueSubstitution};
  }

  prefillAnnualStatementEsignEmailPayload(documentBase: DocumentBase, lodgement: LodgementDeadline): IPrefillResult {
    const payload = new AnnualStatementEsignEmailPayload();

    const emailPayload = this.prefillAnnualStatementEmailPayload(documentBase, lodgement);

    Object.assign(payload, this.omitProperty(emailPayload.payload, '$type'));

    payload.signUrl = '';

    return {payload, ignoredValueSubstitution: emailPayload.ignoredValueSubstitution};
  }

  prefillAnnualStatementEmailPayload(documentBase: DocumentBase, lodgement: LodgementDeadline): IPrefillResult {
    const payload = new AnnualStatementEmailPayload();
    const annualStatement = documentBase as AnnualStatement;
    payload.companyACN = documentBase.company.acn;
    payload.companyName = documentBase.company.name;

    const signatories = documentBase.documentSigning?.signatures[0]
    payload.directorFirstName = signatories?.signatory?.firstName ?? 'N/A';
    payload.directorFullName = signatories?.signatory?.fullNameLabel ?? 'N/A';

    payload.year = annualStatement.reviewDate ? formatDate(new Date(annualStatement.reviewDate) ?? '', 'yyyy', 'en-US') : '';

    payload.annualReviewFeeDeadline = this.#dateFormatPipe.transform(annualStatement.paymentDeadline, DateFormatType.Long);
    payload.annualReviewFeeDeadlineShortFormat = this.#dateFormatPipe.transform(annualStatement.paymentDeadline, DateFormatType.Short);

    payload.debt = this.#currencyPipe.transform(annualStatement.company.companyDebt?.amountOwing ?? 0, '$') ?? '';


    payload.lateFeeForOneMonth = this.#currencyPipe.transform(this.feeValueConfig.lateFeeUnder28 ?? 0, '$') ?? '';
    payload.lateFeeForMoreThanOneMonth = this.#currencyPipe.transform(this.feeValueConfig.lateFeeOver28 ?? 0, '$') ?? '';

    payload.organisationName = this.profile!.organisationName ?? 'N/A';
    payload.organisationEmail = this.profile!.organisationEmail ?? 'N/A';
    payload.organisationPhone = this.profile!.organisationPhone ?? 'N/A';
    payload.username = this.#organisationService.getCurrentUser()?.fullName ?? 'N/A';

    payload.billingContactFirstName = documentBase.company.billingContact?.firstName ?? 'N/A';
    payload.billingContactFullName = documentBase.company.billingContact?.fullName ?? 'N/A';

    payload.partner = this.profile!.users?.find(user => user.id === documentBase.company.partnerManagerId)?.fullName ?? 'N/A';
    payload.accountManager = this.profile!.users?.find(user => user.id === documentBase.company.accountManagerId)?.fullName ?? 'N/A';
    payload.asicContact = '' //TODO get from settings, need to refactor asic contact as it is string

    const ignoredValueSubstitution: string[] = ["directorFirstName", "directorFullName"];

    return {payload, ignoredValueSubstitution};
  }

  private omitProperty<T extends object, K extends keyof T>(obj: T, keyToRemove: K): Omit<T, K> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [keyToRemove]: _, ...filteredObj } = obj;
    return filteredObj;
  }
}
